import { useEffect, useState } from 'react';
import { CrmApi } from '../../api/resources';

/** Statuses, staff and custom-field definitions needed by CRM forms. */
// Statuses, staff and custom fields change rarely but every CRM page needs them, so the
// request is shared between pages and kept for a few minutes. Pages that edit them call
// invalidateCrmLookups(); logout clears it too.
const TTL_MS = 5 * 60 * 1000;
let cached = null; // { promise, at }

export function invalidateCrmLookups() {
  cached = null;
}

function loadLookups() {
  if (cached && Date.now() - cached.at < TTL_MS) return cached.promise;
  const promise = Promise.all([CrmApi.lookups(), CrmApi.customFields.list()]).then(([lookups, fields]) => {
    const active = fields.filter((f) => f.is_active);
    return {
      statuses: lookups.statuses,
      users: lookups.users,
      leadFields: active.filter((f) => f.entity === 'lead'),
      followupFields: active.filter((f) => f.entity === 'followup'),
    };
  });
  cached = { promise, at: Date.now() };
  promise.catch(() => {
    if (cached && cached.promise === promise) cached = null; // never cache a failure
  });
  return promise;
}

export default function useCrmLookups() {
  const [state, setState] = useState({
    statuses: [],
    users: [],
    leadFields: [],
    followupFields: [],
    ready: false,
  });

  useEffect(() => {
    let alive = true;
    loadLookups()
      .then((data) => alive && setState({ ...data, ready: true }))
      .catch(() => alive && setState((s) => ({ ...s, ready: true })));
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

export function StatusBadge({ status }) {
  if (!status) return <span className="muted">—</span>;
  return (
    <span
      className="badge"
      style={{ background: `${status.color}22`, color: status.color }}
    >
      {status.name}
    </span>
  );
}

/** ISO string -> value for <input type="datetime-local"> (local time). */
export function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** <input type="datetime-local"> value (local time) -> UTC ISO string for the API. */
export function fromLocalInput(value) {
  return value ? new Date(value).toISOString() : null;
}

export function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}
